/**
 * Polling that backs off while the API is unreachable (sc-11869).
 *
 * The case editor polls a handful of endpoints every few seconds. When the
 * browser loses the API (network drop, laptop sleep, pod rollout) every tick
 * failed and logged, so one outage became a burst of RUM errors. A poller
 * started here keeps its normal cadence while requests get a response (a 4xx /
 * 5xx included), doubles its delay up to `maxBackoffMs` after each request that
 * got none, and goes back to its cadence on the next success, or at once when
 * the browser comes back online or the tab becomes visible again.
 */
import axios from 'axios';

export const MAX_POLL_BACKOFF_MS = 60_000;

/** A request that got no response (network drop, sleep, rollout). A cancel is not one. */
export function isTransientNetworkError(err: unknown): boolean {
  return axios.isAxiosError(err) && !err.response && err.code !== 'ERR_CANCELED';
}

/** The delay before the next tick after `failures` transient failures in a row. */
export function backoffDelay(intervalMs: number, failures: number, maxMs = MAX_POLL_BACKOFF_MS): number {
  if (failures <= 0) return intervalMs;
  return Math.min(maxMs, intervalMs * 2 ** failures);
}

type Log = Pick<Console, 'error' | 'warn' | 'info'>;

export interface OutageLog {
  /**
   * Logs a failed request. A real HTTP error (one with a response) is
   * console.error'd as before; a request with no response warns once per
   * outage and is silent until `recovered()`.
   */
  error(message: string, err: unknown): void;
  /** A request got through: the next transient failure starts a new outage. */
  recovered(): void;
}

export function createOutageLog(log: Log = console): OutageLog {
  let down = false;
  return {
    error(message, err) {
      if (!isTransientNetworkError(err)) {
        log.error(`${message}:`, err);
        return;
      }
      if (down) return;
      down = true;
      const reason = err instanceof Error ? err.message : String(err);
      log.warn(`${message}: API unreachable (${reason}), polling backs off until it answers`);
    },
    recovered() {
      if (!down) return;
      down = false;
      log.info('API reachable again, polling at normal cadence');
    },
  };
}

export interface PollOptions {
  /** Cadence while healthy, measured from the start of each tick (like setInterval). */
  intervalMs: number;
  maxBackoffMs?: number;
  /** Run the first tick at once instead of after `intervalMs`. */
  immediate?: boolean;
  /** Prefix of the log line for a failed tick. */
  label: string;
  log: OutageLog;
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
const isHidden = () => typeof document !== 'undefined' && document.hidden === true;

/**
 * Runs `tick` on `intervalMs` until the returned stop function is called. A
 * tick that throws is logged through `log`; one that throws a transient
 * network error (or is skipped because the browser is offline) backs off.
 * Resolving to `false` ends the poll. Ticks never overlap.
 */
export function startPolling(tick: () => Promise<unknown>, options: PollOptions): () => void {
  const { intervalMs, maxBackoffMs = MAX_POLL_BACKOFF_MS, immediate = false, label, log } = options;
  let stopped = false;
  let running = false;
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const schedule = (delay: number) => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(run, delay);
  };

  async function run() {
    timer = undefined;
    if (stopped || running) return;
    if (isOffline()) {
      // No request while the browser knows it's offline; `online` wakes us.
      failures += 1;
      schedule(backoffDelay(intervalMs, failures, maxBackoffMs));
      return;
    }
    running = true;
    const startedAt = Date.now();
    let keepGoing = true;
    try {
      keepGoing = (await tick()) !== false;
      failures = 0;
      log.recovered();
    } catch (err) {
      log.error(label, err);
      failures = isTransientNetworkError(err) ? failures + 1 : 0;
    } finally {
      running = false;
    }
    if (stopped || !keepGoing) return;
    schedule(
      failures > 0
        ? backoffDelay(intervalMs, failures, maxBackoffMs)
        : Math.max(0, intervalMs - (Date.now() - startedAt)),
    );
  }

  // Backed off and the browser says the network (or the user) is back: try now.
  const wake = () => {
    if (stopped || running || failures === 0 || isHidden()) return;
    failures = 0;
    schedule(0);
  };
  const hasWindow = typeof window !== 'undefined';
  const hasDocument = typeof document !== 'undefined';
  if (hasWindow) window.addEventListener('online', wake);
  if (hasDocument) document.addEventListener('visibilitychange', wake);

  schedule(immediate ? 0 : intervalMs);

  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
    if (hasWindow) window.removeEventListener('online', wake);
    if (hasDocument) document.removeEventListener('visibilitychange', wake);
  };
}
